import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseAuth } from '@/context/SupabaseAuthContext';
import { Car, Utensils, Power, Save, Hourglass, FileCheck, Zap } from 'lucide-react';

const TRANSPORT_MODES = [
  { label: 'Car', value: 'car', factor: 0.21 }, // kg CO2/km
  { label: 'Bus', value: 'bus', factor: 0.09 },
  { label: 'Bike', value: 'bike', factor: 0 },
  { label: 'Flight', value: 'flight', factor: 0.255 },
  { label: 'Walk', value: 'walk', factor: 0 },
];
const FOOD_TYPES = [
  { label: 'Meat', value: 'meat', factor: 5 }, // kg CO2/serving
  { label: 'Vegetarian', value: 'vegetarian', factor: 2 },
  { label: 'Vegan', value: 'vegan', factor: 1.5 },
  { label: 'Local', value: 'local', factor: 1 },
];

function getToday() {
  return new Date().toISOString().slice(0, 10);
}

interface DailyLogProps {
  onClose?: () => void;
  onDataSaved?: () => void; // Callback to trigger refresh
}

const DailyLog: React.FC<DailyLogProps> = ({ onClose, onDataSaved }) => {
  const { user } = useSupabaseAuth();
  const [date, setDate] = useState(getToday());
  const [transport, setTransport] = useState([{ mode: 'car', distance: '' }]);
  const [home, setHome] = useState({ electricity: '', gas: '', water: '' });
  const [food, setFood] = useState([{ type: 'meat', servings: '' }]);
  const [goods, setGoods] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  // Load selected day's log from Supabase
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    setError('');
    supabase
      .from('carbon_logs')
      .select('*')
      .eq('user_id', user.id)
      .eq('date', date)
      .single()
      .then(({ data, error }) => {
        if (error && error.code !== 'PGRST116') { // Not found is ok
          setError('Failed to load log.');
        }
        if (data) {
          setTransport(data.transport || [{ mode: 'car', distance: '' }]);
          setHome(data.home || { electricity: '', gas: '', water: '' });
          setFood(data.food || [{ type: 'meat', servings: '' }]);
          setGoods(data.goods || '');
        } else {
          setTransport([{ mode: 'car', distance: '' }]);
          setHome({ electricity: '', gas: '', water: '' });
          setFood([{ type: 'meat', servings: '' }]);
          setGoods('');
        }
        setLoading(false);
      });
  }, [date, user]);

  // CO2 calculations
  const transportCO2 = transport.reduce((sum, t) => {
    const mode = TRANSPORT_MODES.find(m => m.value === t.mode);
    const dist = parseFloat(t.distance) || 0;
    return sum + (mode ? mode.factor * dist : 0);
  }, 0);
  const homeCO2 =
    (parseFloat(home.electricity) || 0) * 0.4 + // 0.4 kg CO2/kWh
    (parseFloat(home.gas) || 0) * 0.2 +         // 0.2 kg CO2/kWh
    (parseFloat(home.water) || 0) * 0.0003;     // 0.0003 kg CO2/liter
  const foodCO2 = food.reduce((sum, f) => {
    const type = FOOD_TYPES.find(ft => ft.value === f.type);
    const servings = parseFloat(f.servings) || 0;
    return sum + (type ? type.factor * servings : 0);
  }, 0);
  const goodsCO2 = (parseFloat(goods) || 0) * 0.5; // 0.5 kg CO2 per $ spent
  const totalCO2 = transportCO2 + homeCO2 + foodCO2 + goodsCO2;

  // Handlers
  const handleTransportChange = (idx: number, field: string, value: string) => {
    setTransport(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };
  const handleAddTransport = () => setTransport([...transport, { mode: 'car', distance: '' }]);
  const handleRemoveTransport = (idx: number) => setTransport(transport.filter((_, i) => i !== idx));

  const handleFoodChange = (idx: number, field: string, value: string) => {
    setFood(prev => prev.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };
  const handleAddFood = () => setFood([...food, { type: 'meat', servings: '' }]);
  const handleRemoveFood = (idx: number) => setFood(food.filter((_, i) => i !== idx));

  const handleHomeChange = (field: string, value: string) => {
    setHome(prev => ({ ...prev, [field]: value }));
  };
  const handleGoodsChange = (value: string) => setGoods(value);

  const handleSave = async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    const { error } = await supabase.from('carbon_logs').upsert([
      {
        user_id: user.id,
        date,
        transport,
        home,
        food,
        goods,
        total_co2: totalCO2,
      },
    ], { onConflict: 'user_id,date' });
    setLoading(false);
    if (error) {
      setError('Failed to save log: ' + (error.message || error.details || 'Unknown error'));
    } else {
      setFeedback('Log saved!');
      setTimeout(() => setFeedback(''), 2000);
      
      // Trigger refresh of other components
      if (onDataSaved) {
        onDataSaved();
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="relative bg-white dark:bg-[#181a1b] rounded-2xl shadow-2xl max-w-4xl w-[99vw] md:w-[66vw] p-6 animate-fadeIn">
        {/* Close Button */}
        {onClose && (
          <button
            className="absolute top-4 right-4 text-gray-400 hover:text-[#40e0d0] dark:text-[#fff]text-2xl font-bold focus:outline-none"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        )}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-[#40e0d0] tracking-tight">Daily/Weekly Carbon Log</h3>
        </div>
        <div className="flex flex-col md:flex-row gap-8">
          <div className="flex-1 min-w-0">
            {/* Date Input */}
            <div className="mb-6 flex flex-col sm:flex-row gap-2 items-center">
              <label className="font-medium min-w-[60px]">Date:</label>
              <input
                type="date"
                className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#40e0d0] focus:outline-none dark:text-white dark:bg-[#23272a]"
                value={date}
                onChange={e => setDate(e.target.value)}
              />
            </div>
            {loading && <div className="text-muted-foreground mb-2">Loading...</div>}
            {error && <div className="text-red-500 mb-2">{error}</div>}
            {/* Transport Section */}
            <div className="mb-8">
              <h4 className="font-semibold mb-3 text-lg flex items-center gap-2">
                <Car className="h-4 text-[#40e0d0]" />
                Transportation
              </h4>
              <div className="flex flex-col gap-2">
                {transport.map((t, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_120px_40px] gap-2 items-center">
                    <select
                      className="border rounded-lg px-2 py-2 focus:ring-2 focus:ring-[#40e0d0] appearance-none dark:text-white dark:bg-[#23272a]"
                      value={t.mode}
                      onChange={e => handleTransportChange(idx, 'mode', e.target.value)}
                    >
                      {TRANSPORT_MODES.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0"
                      className="border rounded-lg px-2 py-2 w-full focus:ring-2 focus:ring-[#40e0d0] dark:text-white dark:bg-[#23272a]"
                      placeholder="Distance (km)"
                      value={t.distance}
                      onChange={e => handleTransportChange(idx, 'distance', e.target.value)}
                    />
                    {transport.length > 1 && (
                      <button className="text-red-500 text-lg hover:bg-red-50 rounded-full w-8 h-8 flex items-center justify-center" onClick={() => handleRemoveTransport(idx)} title="Remove">
                        <span aria-hidden>×</span>
                      </button>
                    )}
                  </div>
                ))}
                <button className="text-[#40e0d0] text-sm mt-1 flex items-center gap-1 hover:underline" onClick={handleAddTransport}>
                  <span className="text-lg">＋</span> Add Transport
                </button>
              </div>
            </div>
            <hr className="my-4 border-gray-200 dark:border-gray-700" />
            {/* Home Energy Section */}
            <div className="mb-8">
              <h4 className="font-semibold mb-3 text-lg flex items-center gap-2">
                <Zap className="h-4 text-[#40e0d0]" />
                Home Energy
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  type="number"
                  min="0"
                  className="border rounded-lg px-2 py-2 focus:ring-2 focus:ring-[#40e0d0] dark:text-white dark:bg-[#23272a]"
                  placeholder="Electricity (kWh)"
                  value={home.electricity}
                  onChange={e => handleHomeChange('electricity', e.target.value)}
                />
                <input
                  type="number"
                  min="0"
                  className="border rounded-lg px-2 py-2 focus:ring-2 focus:ring-[#40e0d0] dark:text-white dark:bg-[#23272a]"
                  placeholder="Gas (kWh)"
                  value={home.gas}
                  onChange={e => handleHomeChange('gas', e.target.value)}
                />
                <input
                  type="number"
                  min="0"
                  className="border rounded-lg px-2 py-2 focus:ring-2 focus:ring-[#40e0d0] dark:text-white dark:bg-[#23272a]"
                  placeholder="Water (liters)"
                  value={home.water}
                  onChange={e => handleHomeChange('water', e.target.value)}
                />
              </div>
            </div>
            <hr className="my-4 border-gray-200 dark:border-gray-700" />
            {/* Food Section */}
            <div className="mb-8">
              <h4 className="font-semibold mb-3 text-lg flex items-center gap-2">
                <Utensils className="h-4 text-[#40e0d0]" />
                Food Choices
              </h4>
              <div className="flex flex-col gap-2">
                {food.map((f, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_120px_40px] gap-2 items-center">
                    <select
                      className="border rounded-lg px-2 py-2 focus:ring-2 focus:ring-[#40e0d0] appearance-none dark:text-white dark:bg-[#23272a]"
                      value={f.type}
                      onChange={e => handleFoodChange(idx, 'type', e.target.value)}
                    >
                      {FOOD_TYPES.map(ft => (
                        <option key={ft.value} value={ft.value}>{ft.label}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0"
                      className="border rounded-lg px-2 py-2 w-full focus:ring-2 focus:ring-[#40e0d0] dark:text-white dark:bg-[#23272a]"
                      placeholder="Servings"
                      value={f.servings}
                      onChange={e => handleFoodChange(idx, 'servings', e.target.value)}
                    />
                    {food.length > 1 && (
                      <button className="text-red-500 text-lg hover:bg-red-50 rounded-full w-8 h-8 flex items-center justify-center" onClick={() => handleRemoveFood(idx)} title="Remove">
                        <span aria-hidden>×</span>
                      </button>
                    )}
                  </div>
                ))}
                <button className="text-[#40e0d0] text-sm mt-1 flex items-center gap-1 hover:underline" onClick={handleAddFood}>
                  <span className="text-lg">＋</span> Add Food
                </button>
              </div>
            </div>
            <hr className="my-4 border-gray-200 dark:border-gray-700" />
            {/* Goods & Services Section */}
            <div className="mb-8">
              <h4 className="font-semibold mb-3 text-lg flex items-center gap-2">
                <span role="img" aria-label="Goods">🛍️</span>
                Goods & Services
              </h4>
              <div className="flex flex-col gap-2">
                <input
                  type="number"
                  min="0"
                  className="border rounded-lg px-2 py-2 focus:ring-2 focus:ring-[#40e0d0] dark:text-white dark:bg-[#23272a] w-full max-w-xs"
                  placeholder="Estimated spending ($)"
                  value={goods}
                  onChange={e => handleGoodsChange(e.target.value)}
                />
                <span className="text-left text-xs text-muted-foreground">Estimated: 0.5 kg CO₂ per $ spent</span>
              </div>
            </div>
            
          </div>
          {/* Summary on the right */}
          <div className="w-full md:w-80 flex-shrink-0">
            <div className="mt-0 p-5 rounded-md dark:bg-[#23272a] border border-[#40e0d0]/20 shadow">
              <h4 className="font-semibold mb-3 text-lg flex items-center gap-2">
                <FileCheck />
                Summary for {date}
              </h4>
              <ul className="text-left mb-2 space-y-1">
                <li>Transportation: <span className="font-mono text-[#40e0d0]">{transportCO2.toFixed(2)} kg CO₂</span></li>
                <li>Home Energy: <span className="font-mono text-[#40e0d0]">{homeCO2.toFixed(2)} kg CO₂</span></li>
                <li>Food: <span className="font-mono text-[#40e0d0]">{foodCO2.toFixed(2)} kg CO₂</span></li>
                <li>Goods & Services: <span className="font-mono text-[#40e0d0]">{goodsCO2.toFixed(2)} kg CO₂</span></li>
              </ul>
              <div className="font-bold text-2xl text-[#388e3c] mt-8 mb-1">Total: {totalCO2.toFixed(2)} kg CO₂</div>
            </div>
            {/* Tips Section */}
            <div className="mt-6 p-4 rounded-md bg-[#e0f7fa] dark:bg-[#20302e] border border-[#40e0d0]/10 shadow-sm">
              <h5 className="font-semibold mb-2 text-[#40b0d0] text-left flex items-center gap-2">
                <span role="img" aria-label="Tips">💡</span> Tips for Your Daily Log
              </h5>
              <ul className="text-left list-disc pl-5 text-xs text-gray-700 dark:text-gray-200 space-y-1">
                <li>Estimate distances for travel using map apps or your phone's step counter.</li>
                <li>Check your utility bill or smart meter for accurate home energy use.</li>
                <li>Count all main meals and snacks for food servings.</li>
                <li>Include online purchases and subscriptions in Goods & Services.</li>
                <li>If unsure, make your best guess—logging something is better than nothing!</li>
                <li>Try to log at the end of each day for best accuracy.</li>
              </ul>
            </div>
            {/* Save Button & Feedback */}
            <div className="flex flex-col sm:flex-row text-right gap-2 mt-[4rem] sm:justify-end">
              <button
                className="bg-[#40e0d0]/20 text-[#40e0d0] px-8 py-2 rounded-xl border border-[#40e0d0] hover:bg-white transition flex items-center gap-2"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? (
                  <Hourglass className="animate-spin mr-2" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                {loading ? 'Saving...' : 'Save Log'}
              </button>
              {feedback && <div className="text-green-600 font-semibold">{feedback}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyLog;